import { useId, useState } from "react";
import type { FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { Loading } from "../../components/ui";
import { t } from "../../i18n/t";
import { routes, safeNext } from "../../lib/routes";
import { usePageTitle } from "../../lib/usePageTitle";

export function LoginPage() {
  const { status, login } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const id = useId();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const next = safeNext(params.get("neste"));
  usePageTitle(t("auth.loginTitle"));

  if (status === "loading") return <Loading />;
  if (status === "authenticated" && !busy) return <Navigate to={next} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email.trim(), password);
      navigate(next, { replace: true });
    } catch (err) {
      const apiError = toApiError(err);
      // Generic text on 401 so the form never reveals whether an account exists.
      setError(
        apiError.status === 401 || apiError.status === 422
          ? t("auth.invalidCredentials")
          : apiError.status === 429
            ? t("auth.rateLimited")
            : apiError.userMessage,
      );
      setBusy(false);
    }
  }

  return (
    <div className="auth-card card">
      <h1>{t("auth.loginTitle")}</h1>
      <p className="muted">{t("auth.loginIntro")}</p>
      <form onSubmit={(e) => void onSubmit(e)} aria-describedby={error ? `${id}-error` : undefined}>
        <div className="field">
          <label htmlFor={`${id}-email`}>{t("auth.email")}</label>
          <input
            id={`${id}-email`}
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`${id}-password`}>{t("auth.password")}</label>
          <input
            id={`${id}-password`}
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error && (
          <p id={`${id}-error`} className="notice notice-error" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
            {busy ? t("auth.loggingIn") : t("auth.submitLogin")}
          </button>
        </div>
      </form>
      <p>
        <Link to={routes.forgotPassword()}>{t("auth.forgotLink")}</Link>
      </p>
    </div>
  );
}
